import React, { useMemo } from 'react';
import { IoBusiness, IoSparkles, IoMapOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { getCityNameKey } from '../../city/constants';
import { BASE_KEYS, BASE_CITY_COORDINATES } from '../constants';

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km) {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  if (km < 10) return `${km.toFixed(1)}km`;
  return `${Math.round(km)}km`;
}

export default function BaseCard({ base, onClick, onMapClick, myPos, imageLoading = 'lazy' }) {
  const { t } = useTranslation();
  const cityName = t(getCityNameKey(base.cityId));
  const availabilityKey = base.isMyBase
    ? BASE_KEYS.SLOT_OCCUPYING
    : base.isFull
      ? BASE_KEYS.SLOT_FULL
      : BASE_KEYS.SLOT_AVAILABLE;

  const distanceLabel = useMemo(() => {
    if (!myPos) return null;
    const coord = BASE_CITY_COORDINATES[base.cityId];
    if (!coord) return null;
    const km = haversineKm(myPos.lat, myPos.lng, coord.lat, coord.lng);
    return formatDistance(km);
  }, [myPos, base.cityId]);

  return (
    <div
      role="button"
      tabIndex={0}
      data-base-city={base.cityId}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      style={{
        background: base.isMyBase
          ? 'rgba(237, 233, 254, 0.6)'
          : 'rgba(255, 255, 255, 0.9)',
        borderRadius: 10,
        padding: '4px 10px 4px',
        border: 'none',
        boxShadow: base.isMyBase
          ? '0 4px 12px rgba(108, 92, 231, 0.14)'
          : '0 4px 12px rgba(0, 0, 0, 0.06)',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        borderLeft: base.isMyBase ? '4px solid #6C5CE7' : undefined,
      }}
    >
      {/* Header: BASE 배지 + MY BASE 배지 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: '0.7rem',
          fontWeight: 700,
          color: '#6C3EAF',
          background: '#EDE9FE',
          padding: '3px 8px',
          borderRadius: 6,
        }}>
          <IoBusiness size={11} />
          BASE
        </div>
        {base.isMyBase && (
          <span style={{
            fontSize: '0.65rem',
            fontWeight: 900,
            color: '#6C5CE7',
            background: '#EDE9FE',
            padding: '3px 7px',
            borderRadius: 999,
          }}>
            {t(BASE_KEYS.MY_BASE)}
          </span>
        )}
      </div>

      {/* Body: 텍스트 좌측, 이미지 우측 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 도시명 (좌) + 보너스 배지 + 슬롯 카운트 (우) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: '0.88rem',
              fontWeight: 900,
              color: '#111827',
              letterSpacing: '-0.2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}>
              {cityName}
            </span>
            {base.totalBonusPercent > 0 && (
              <span style={{
                fontSize: '0.68rem',
                fontWeight: 800,
                color: '#92400E',
                background: '#FEF3C7',
                padding: '2px 6px',
                borderRadius: 999,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
              }}>
                <IoSparkles size={10} />
                {`+${base.totalBonusPercent}%`}
              </span>
            )}
            <span style={{
              fontSize: '0.8rem',
              fontWeight: 800,
              color: base.isFull ? '#EF4444' : '#374151',
              flexShrink: 0,
            }}>
              {base.occupiedCount}
              <span style={{ fontWeight: 500, color: '#9CA3AF', fontSize: '0.72rem' }}>
                {' / '}{base.slotsPerCity}
              </span>
            </span>
          </div>

          {/* 점유 상태 칩 */}
          <span style={{
            display: 'inline-block',
            alignSelf: 'flex-start',
            fontSize: '0.68rem',
            color: base.isMyBase ? '#6C5CE7' : base.isFull ? '#EF4444' : '#059669',
            background: base.isMyBase ? '#EDE9FE' : base.isFull ? '#FEE2E2' : '#D1FAE5',
            padding: '3px 7px',
            borderRadius: 999,
            fontWeight: 800,
          }}>
            {t(availabilityKey)}
          </span>
        </div>

        <div style={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 12,
          overflow: 'hidden',
          background: '#F3F4F6',
          border: '1px solid rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {base.thumbnailUrl || base.imageUrl ? (
            <img
              src={base.thumbnailUrl || base.imageUrl}
              alt={cityName}
              loading={imageLoading}
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <IoBusiness size={22} color="#6C5CE7" />
          )}
        </div>
      </div>

      {/* Footer: 거리 + 지도버튼 (좌) | 보너스 칩 (우, 있을 때만) */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #F3F4F6',
        paddingTop: 4,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* 거리 (위치 있을 때만) */}
          {distanceLabel && (
            <span style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#6B7280',
              flexShrink: 0,
            }}>
              {distanceLabel}
            </span>
          )}

          {/* 지도 버튼 */}
          {onMapClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onMapClick(base.cityId); }}
              style={{
                background: '#6C5CE7',
                border: 'none',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                flexShrink: 0,
              }}
            >
              <IoMapOutline size={14} color="white" />
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
